import { computed, nextTick, Ref, ref, watch } from 'vue';
import api from '@/api';
import { PageName, SummaryInfo } from '@/api/tracking';
import {
  FeedSummaryResponse,
  SummarizableFeature,
  SurveyHistoryStatus,
} from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { useFeedSummaryProgress } from './use-feed-summary-progress';

export const useFeedSummaryCollapse = (
  props: {
    feedSummary: FeedSummaryResponse;
    feature: SummarizableFeature;
    pageName: PageName;
    // 閉じているときにメッセージを表示する場合は、閉じても中身を描画したままにする
    showMessageWhenClosed?: boolean;
    autoOpenWhenGenerated?: boolean;
  },
  status: Ref<SurveyHistoryStatus>,
  summaryInfo: Ref<SummaryInfo | undefined>,
) => {
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const layoutVariant = useLayoutVariant();

  const displayIsInProgress = ref(status.value.isInProgress);

  const { progressBarWidth, startProgress, stopProgress } =
    useFeedSummaryProgress(
      computed(() => {
        if (props.feedSummary.generation_status === 'unprocessed')
          return undefined;
        return props.feedSummary.id;
      }),
    );

  if (status.value.isInProgress) {
    startProgress();
  }

  watch(
    () => status.value.isInProgress,
    progress => {
      // プログレスバーの開始と停止
      if (progress) {
        startProgress();
      } else {
        stopProgress();
      }

      // progressでない状態から戻ったときに状態を戻す
      if (!displayIsInProgress.value && progress) {
        isClose.value = false;
      }

      // アニメーションのためにnextTickで遅延させる
      nextTick(() => (displayIsInProgress.value = progress));
    },
  );

  // 生成済みなら閉じた状態にしておく（autoOpenWhenGeneratedが指定されている場合を除く）
  const isClose = ref(
    props.feedSummary.generation_status === 'processed' &&
      !props.autoOpenWhenGenerated,
  );
  const hasOpened = ref(props.showMessageWhenClosed || !isClose.value);

  const trackSummaryToggle = (name: 'summary_collapse' | 'summary_expand') => {
    if (summaryInfo.value === undefined) return;

    api.trackEvent(
      name,
      {
        feature: props.feature,
        pageName: props.pageName,
        pageUrl: getCurrentPathForTracking(),
        layoutVariant,
      },
      undefined,
      undefined,
      summaryInfo.value,
    );
  };

  const toggleClose = () => {
    if (status.value.isInProgress) return;

    isClose.value = !isClose.value;
    trackSummaryToggle(isClose.value ? 'summary_collapse' : 'summary_expand');
    if (isClose.value && !props.showMessageWhenClosed) {
      hasOpened.value = false;
    }
  };

  const closeTransitionEnd = () => {
    if (!isClose.value) {
      hasOpened.value = true;
    }
  };

  return {
    isClose,
    hasOpened,
    displayIsInProgress,
    progressBarWidth,
    toggleClose,
    closeTransitionEnd,
  };
};
